import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import Header from "../components/Header";
import { Ionicons } from "@expo/vector-icons";

const AdminOrders = ({ navigation }) => {
  const [orders, setOrders] = useState([]);
  const [clientNames, setClientNames] = useState({});
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, "orders"),
          where("estado", "in", ["Procesando", "Pendiente"])
        );
        const snap = await getDocs(q);
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
        setOrders(list);

        // Fetch client names for unique users
        const uniqueUsers = [...new Set(list.map((o) => o.user).filter(Boolean))];
        const nameMap = {};
        await Promise.all(
          uniqueUsers.map(async (email) => {
            try {
              const snap = await getDoc(doc(db, "users", email));
              if (snap.exists()) {
                nameMap[email] = snap.data()?.data?.APELLIDO ?? null;
              }
            } catch {
              // leave null
            }
          })
        );
        setClientNames(nameMap);
      } catch (e) {
        console.error("Error fetching orders:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const toggleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = () => {
    const allSelected = orders.every((o) => selected[o.id]);
    const next = {};
    orders.forEach((o) => { next[o.id] = !allSelected; });
    setSelected(next);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const bulkFinalizar = async () => {
    const toFinalize = orders.filter((o) => selected[o.id]);
    if (toFinalize.length === 0) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      toFinalize.forEach((o) =>
        batch.update(doc(db, "orders", o.id), { estado: "Finalizado" })
      );
      await batch.commit();
      setOrders((prev) => prev.filter((o) => !selected[o.id]));
      setSelected({});
    } catch (e) {
      console.error("Error bulk updating:", e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <View style={s.centered}>
        <ActivityIndicator size="large" color="#080680" />
        <Text style={s.loadingText}>Cargando pedidos...</Text>
      </View>
    );
  }

  return (
    <View style={s.container}>
      <Header title="Gestionar Pedidos" navigation={navigation} isBack={true} />

      {orders.length === 0 ? (
        <View style={s.centered}>
          <Ionicons name="checkmark-done-circle-outline" size={64} color="#4CAF50" />
          <Text style={s.emptyText}>No hay pedidos pendientes</Text>
        </View>
      ) : (
        <>
          <View style={s.toolbar}>
            <TouchableOpacity style={s.selectAllBtn} onPress={toggleAll}>
              <Ionicons
                name={orders.every((o) => selected[o.id]) ? "checkbox" : "square-outline"}
                size={20}
                color="#080680"
              />
              <Text style={s.selectAllText}>Seleccionar todos</Text>
            </TouchableOpacity>
            <Text style={s.countHint}>{orders.length} pedido{orders.length !== 1 ? "s" : ""}</Text>
            <TouchableOpacity
              style={[s.finalizarBtn, selectedCount === 0 && s.finalizarBtnDisabled]}
              onPress={bulkFinalizar}
              disabled={selectedCount === 0 || saving}
            >
              {saving ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={s.finalizarBtnText}>Finalizar ({selectedCount})</Text>
              )}
            </TouchableOpacity>
          </View>

          <ScrollView
            contentContainerStyle={s.list}
            showsVerticalScrollIndicator={false}
          >
            {orders.map((item) => {
              const isSelected = !!selected[item.id];
              const dateObj = new Date(item.fecha);
              const dateStr = dateObj.toLocaleDateString("es-AR", {
                day: "2-digit", month: "2-digit", year: "numeric",
              });
              const timeStr = dateObj.toLocaleTimeString("es-AR", {
                hour: "2-digit", minute: "2-digit",
              });
              const cuit = item.user?.replace("@mail.com", "") ?? "—";
              const apellido = clientNames[item.user] ?? null;
              const isPendiente = item.estado === "Pendiente";

              return (
                <TouchableOpacity
                  key={item.id}
                  style={[s.card, isSelected && s.cardSelected]}
                  onPress={() => toggleSelect(item.id)}
                  activeOpacity={0.85}
                >
                  {/* Checkbox */}
                  <View style={s.checkArea}>
                    <Ionicons
                      name={isSelected ? "checkbox" : "square-outline"}
                      size={22}
                      color={isSelected ? "#080680" : "#ccc"}
                    />
                  </View>

                  {/* Body */}
                  <View style={s.cardBody}>
                    {/* Row 1: client + status */}
                    <View style={s.row}>
                      <View style={s.clientBlock}>
                        {apellido ? (
                          <Text style={s.clientName}>{apellido}</Text>
                        ) : null}
                        <Text style={apellido ? s.cuit : s.clientName}>{cuit}</Text>
                      </View>
                      <View style={[s.statusBadge, isPendiente ? s.badgePendiente : s.badgeProcesando]}>
                        <Text style={[s.statusText, isPendiente ? s.statusTextPendiente : s.statusTextProcesando]}>
                          {item.estado}
                        </Text>
                      </View>
                    </View>

                    {/* Row 2: date + total */}
                    <View style={s.row}>
                      <View style={s.dateContainer}>
                        <Ionicons name="calendar-outline" size={13} color="#999" />
                        <Text style={s.dateText}>{dateStr} · {timeStr}</Text>
                      </View>
                      <Text style={s.totalText}>${Number(item.total).toFixed(2)}</Text>
                    </View>

                    {/* Row 3: ERO / DRN / items */}
                    <View style={s.row}>
                      <View style={s.tagsRow}>
                        {item.ero && Number(item.ero) > 0 && (
                          <View style={s.tag}>
                            <Text style={s.tagText}>ERO ${Number(item.ero).toFixed(0)}</Text>
                          </View>
                        )}
                        {item.drn && Number(item.drn) > 0 && (
                          <View style={[s.tag, s.tagDrn]}>
                            <Text style={[s.tagText, s.tagTextDrn]}>DRN ${Number(item.drn).toFixed(0)}</Text>
                          </View>
                        )}
                        {Array.isArray(item.items) && (
                          <View style={s.tagNeutral}>
                            <Text style={s.tagTextNeutral}>{item.items.length} ítem{item.items.length !== 1 ? "s" : ""}</Text>
                          </View>
                        )}
                      </View>
                      {item.viajante && item.viajante !== "No" && (
                        <View style={s.viajanteTag}>
                          <Text style={s.viajanteText}>Viajante</Text>
                        </View>
                      )}
                    </View>

                    {/* Discount */}
                    {item.discount && Number(item.discount) > 0 && (
                      <Text style={s.discountText}>
                        Descuento: -${Number(item.discount).toFixed(2)}
                      </Text>
                    )}
                  </View>

                  {/* Detail arrow */}
                  <TouchableOpacity
                    style={s.detailBtn}
                    onPress={() => navigation.navigate("Order", item)}
                  >
                    <Ionicons name="chevron-forward" size={20} color="#bbb" />
                  </TouchableOpacity>
                </TouchableOpacity>
              );
            })}
            <View style={{ height: 40 }} />
          </ScrollView>
        </>
      )}
    </View>
  );
};

export default AdminOrders;

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F4F6FD" },
  centered: { flex: 1, justifyContent: "center", alignItems: "center" },
  loadingText: { marginTop: 10, color: "#666" },
  emptyText: { marginTop: 16, fontSize: 16, color: "#999" },

  toolbar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    gap: 8,
  },
  selectAllBtn: { flexDirection: "row", alignItems: "center", gap: 6 },
  selectAllText: { fontSize: 14, color: "#080680", fontWeight: "600" },
  countHint: { flex: 1, fontSize: 12, color: "#aaa", textAlign: "center" },
  finalizarBtn: {
    backgroundColor: "#4CAF50",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
  },
  finalizarBtnDisabled: { backgroundColor: "#ccc" },
  finalizarBtnText: { color: "#fff", fontWeight: "700", fontSize: 14 },

  list: { padding: 16 },

  card: {
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 2,
    borderColor: "transparent",
  },
  cardSelected: { borderColor: "#080680", backgroundColor: "#F0F4FF" },
  checkArea: { marginRight: 12, paddingTop: 2 },
  cardBody: { flex: 1, gap: 8 },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  clientBlock: { gap: 1 },
  clientName: { fontSize: 15, fontWeight: "700", color: "#1a1a1a" },
  cuit: { fontSize: 12, color: "#888" },

  statusBadge: {
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
  },
  badgeProcesando: { backgroundColor: "#FFF3E0", borderColor: "#FF9800" },
  badgePendiente: { backgroundColor: "#FFF8E1", borderColor: "#FFC107" },
  statusText: { fontSize: 11, fontWeight: "700" },
  statusTextProcesando: { color: "#E65100" },
  statusTextPendiente: { color: "#F57F17" },

  dateContainer: { flexDirection: "row", alignItems: "center", gap: 4 },
  dateText: { fontSize: 12, color: "#888" },
  totalText: { fontSize: 14, fontWeight: "700", color: "#080680" },

  tagsRow: { flexDirection: "row", gap: 6, flexWrap: "wrap" },
  tag: {
    backgroundColor: "#EEF0FB",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  tagText: { fontSize: 11, color: "#080680", fontWeight: "600" },
  tagDrn: { backgroundColor: "#FFF3E0" },
  tagTextDrn: { color: "#E65100" },
  tagNeutral: {
    backgroundColor: "#F5F5F5",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  tagTextNeutral: { fontSize: 11, color: "#666" },

  viajanteTag: {
    backgroundColor: "#F3E5F5",
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  viajanteText: { fontSize: 11, color: "#7B1FA2", fontWeight: "600" },

  discountText: { fontSize: 12, color: "#4CAF50", fontWeight: "500" },

  detailBtn: { paddingLeft: 8, paddingTop: 2 },
});
